import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { createOverseasEstimate } from '../../api/overseas';
import { brown, orange } from '../../pages/en/enCommon';

/*
 * 해외 손님 견적 문의 양식 (2026-11-04 사장님: "해외는 메일로 문의하도록 알려주고 일정 쓰는 메모 칸을 많이 쓸수 있게
 * 해주고 첨부파일도 올릴수 있게 해줘. 한 5메가정도").
 * 한국 휴대폰 본인확인이 없다 — 답은 메일로 간다. 서버 검사가 진짜이고 여기 검사는 손님 편의다.
 * 서버와 맞춰야 하는 값: 일정 20,000자, 첨부 5MB·종류(OverseasEstimateService).
 */
const ITINERARY_MAX = 20000;
const FILE_MAX = 5 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.hwpx,.txt,.csv,.zip';

const EMPTY = {
  name: '', email: '', phone: '', companyName: '', country: '',
  memberCount: '', departDate: '', arrivalDate: '', departPlace: '', arrivalPlace: '',
  vehicle: '', itinerary: '',
  website: '', // 허니팟 — 사람에게는 안 보인다
};

const PLACEHOLDER = `For example:
Day 1 (Dec 1) 10:30 Pick up at Incheon Airport T1 → Hotel in Myeongdong, Seoul
Day 2 (Dec 2) 09:00 Hotel → Gyeongbokgung Palace → N Seoul Tower → Hotel (until 18:00)
Day 3 (Dec 3) 08:00 Hotel → Nami Island → Seoul
Day 4 (Dec 4) 07:00 Hotel → Incheon Airport

Luggage: 30 large suitcases. Child seats: none. Anything else we should know.`;

export default function OverseasInquiryForm() {
  const [v, setV] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const fileInput = useRef(null);

  const set = (k) => (e) => setV((prev) => ({ ...prev, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setError('');
    if (!f) return setFile(null);
    if (f.size > FILE_MAX) {
      e.target.value = '';
      setFile(null);
      return setError('The file is too large. Maximum size is 5 MB.');
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!v.name.trim() || !v.email.trim() || !v.itinerary.trim()) {
      return setError('Please fill in your name, email and trip details.');
    }
    if (!agree) return setError('Please agree to the use of your personal information for the quotation.');
    setSending(true);
    try {
      const members = Math.max(0, Math.min(2000, parseInt(v.memberCount, 10) || 0));   // 1.5·1e2 같은 값도 정수로
      const res = await createOverseasEstimate({ ...v, memberCount: members }, file);
      setDone({ id: res && res.id, email: v.email.trim() });
      setV(EMPTY);
      setFile(null);
      setAgree(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const status = err && err.response && err.response.status;
      const msg = err && err.response && err.response.data && err.response.data.message;
      if (msg && status === 400) setError(msg);
      else if (status === 413) setError('The file is too large. Maximum size is 5 MB.');
      else if (status === 429) setError('Too many requests. Please try again in a few minutes, or email us directly.');
      else setError('Sorry, something went wrong. Please try again, or email us directly.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <Box sx={{ p: { xs: 2.5, md: 4 }, border: `2px solid ${orange}`, borderRadius: 3, textAlign: 'center', bgcolor: '#FFFDF6' }}>
        <Typography component='h2' sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: brown }}>
          Thank you! Your inquiry has been received.
        </Typography>
        {done.id && (
          <Typography sx={{ mt: 1, color: brown }}>Reference no. <b>#{done.id}</b></Typography>
        )}
        <Typography sx={{ mt: 1.5, color: brown }}>
          We will review your trip and reply by email to <b>{done.email}</b>.
          <br />Please check your spam folder if you do not see our reply.
        </Typography>
        <Button onClick={() => setDone(null)} sx={{ mt: 2, color: orange, fontWeight: 700 }}>
          Send another inquiry
        </Button>
      </Box>
    );
  }

  const field = (k, label, props = {}) => (
    <TextField
      fullWidth size='small' label={label} value={v[k]} onChange={set(k)}
      inputProps={{ maxLength: 200 }} {...props}
    />
  );

  return (
    <Box component='form' onSubmit={submit} noValidate>
      {/* 허니팟 — 화면 밖으로 빼고 보조기기·탭 이동에서도 뺀다 */}
      <Box aria-hidden='true' sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          Website
          <input type='text' tabIndex={-1} autoComplete='off' value={v.website} onChange={set('website')} />
        </label>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>{field('name', 'Your name *', { autoComplete: 'name', inputProps: { maxLength: 100 } })}</Grid>
        <Grid item xs={12} sm={6}>{field('email', 'Email *', { type: 'email', autoComplete: 'email' })}</Grid>
        <Grid item xs={12} sm={6}>{field('phone', 'Phone / WhatsApp (optional)', { type: 'tel', autoComplete: 'tel', inputProps: { maxLength: 30 } })}</Grid>
        <Grid item xs={12} sm={6}>{field('country', 'Country (optional)', { autoComplete: 'country-name' })}</Grid>
        <Grid item xs={12}>{field('companyName', 'Company / group name (optional)', { autoComplete: 'organization' })}</Grid>

        <Grid item xs={6} sm={3}>{field('departDate', 'Start date', { type: 'date', InputLabelProps: { shrink: true } })}</Grid>
        <Grid item xs={6} sm={3}>{field('arrivalDate', 'End date', { type: 'date', InputLabelProps: { shrink: true } })}</Grid>
        <Grid item xs={6} sm={3}>{field('memberCount', 'Passengers', { type: 'number', inputProps: { min: 0, max: 2000 } })}</Grid>
        <Grid item xs={6} sm={3}>
          <TextField select fullWidth size='small' label='Bus size' value={v.vehicle} onChange={set('vehicle')}>
            <MenuItem value=''>Not sure</MenuItem>
            <MenuItem value='25'>25-seater</MenuItem>
            <MenuItem value='28'>28-seater limousine</MenuItem>
            <MenuItem value='45'>45-seater</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>{field('departPlace', 'Pick-up location (e.g. Incheon Airport)')}</Grid>
        <Grid item xs={12} sm={6}>{field('arrivalPlace', 'Destination (e.g. Seoul, Busan)')}</Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth multiline minRows={10} maxRows={30}
            label='Trip details / itinerary *'
            placeholder={PLACEHOLDER}
            value={v.itinerary}
            onChange={set('itinerary')}
            inputProps={{ maxLength: ITINERARY_MAX }}
            helperText={`Day by day schedule, times, places, luggage, anything else. ${v.itinerary.length.toLocaleString()} / ${ITINERARY_MAX.toLocaleString()}`}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant='outlined' startIcon={<AttachFileIcon />} onClick={() => fileInput.current && fileInput.current.click()}
              sx={{ color: brown, borderColor: orange }}>
              Attach a file
            </Button>
            <input ref={fileInput} type='file' accept={ACCEPT} onChange={pickFile} hidden />
            <Typography sx={{ fontSize: 14, color: '#777' }}>
              {file ? `${file.name} (${file.size >= 1024 * 1024 ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(file.size / 1024)} KB`})` : 'Optional · itinerary, passenger list, etc. · max 5 MB'}
            </Typography>
            {file && (
              <Button size='small' onClick={() => { setFile(null); if (fileInput.current) fileInput.current.value = ''; }}>
                Remove
              </Button>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 14 }}>
                I agree that EK Tour Co., Ltd. may use my name, email, phone and trip details to prepare a quotation and reply to me.
                They are kept for 1 year and then deleted. (<a href='/privacy' target='_blank' rel='noopener noreferrer'>Privacy policy, Korean</a>)
              </Typography>
            }
          />
        </Grid>

        {error && (
          <Grid item xs={12}><Alert severity='error'>{error}</Alert></Grid>
        )}

        <Grid item xs={12}>
          <Button type='submit' variant='contained' disabled={sending} fullWidth
            sx={{ py: 1.4, fontSize: 17, fontWeight: 800, bgcolor: orange, '&:hover': { bgcolor: '#d98a30' } }}>
            {sending ? 'Sending…' : 'Send inquiry — we reply by email'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
